// src/ui/screens/EditProfileScreen.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserAvatar } from '../../components/UserAvatar';
import { userProfileService } from '../../services/user/userProfileService';
import { authService } from '../../services/auth/authService';
import { chatLocalDb } from '../../services/chat/chatLocalDb';
import { db, storage } from '../../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
  deleteField,
  serverTimestamp,
  arrayRemove,
  FieldValue,
  Timestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  red: '#E05252',
};

const MAX_NAME_LENGTH = 21;
const MAX_USERNAME_LENGTH = 21;
const MAX_BIO_LENGTH = 80;
const USERNAME_CHANGE_DAYS = 14;

// Desktop avatar is rendered larger than the mobile one (130px vs 100px).
// UserAvatar takes a pixel `size` prop rather than a className, so this
// can't be done with a Tailwind breakpoint alone — a small resize listener
// picks the right size. Mobile always gets 100, unchanged. Same pattern
// used in ProfileScreen.jsx.
const DESKTOP_BREAKPOINT = 768;
const MOBILE_AVATAR_SIZE = 100;
const DESKTOP_AVATAR_SIZE = 130;

// ── EditProfileScreen ────────────────────────────────────────────────────────

export const EditProfileScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [bio, setBio] = useState('');
  const [link, setLink] = useState('');
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [pickedImage, setPickedImage] = useState(null);
  const [pickedImageFile, setPickedImageFile] = useState(null);
  const [usernameError, setUsernameError] = useState(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [lastUsernameChange, setLastUsernameChange] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  // Desktop-only avatar sizing — see DESKTOP_AVATAR_SIZE note above.
  const [avatarSize, setAvatarSize] = useState(MOBILE_AVATAR_SIZE);

  const fileInputRef = useRef(null);

  // ── Computed values ──────────────────────────────────────────────────────

  const canChangeUsername = !lastUsernameChange ||
    (Date.now() - new Date(lastUsernameChange).getTime()) / (1000 * 60 * 60 * 24) >= USERNAME_CHANGE_DAYS;

  const daysUntilUsernameChange = lastUsernameChange
    ? Math.max(0, USERNAME_CHANGE_DAYS - Math.floor(
        (Date.now() - new Date(lastUsernameChange).getTime()) / (1000 * 60 * 60 * 24)
      ))
    : 0;

  const isUsernameValid = () => {
    const val = username.trim();
    if (val === originalUsername) return true;
    if (val.length < 2 || val.length > MAX_USERNAME_LENGTH) return false;
    if (usernameError) return false;
    if (isCheckingUsername) return false;
    if (!canChangeUsername) return false;
    return true;
  };

  const isFormValid = () => {
    const nameVal = name.trim();
    return nameVal.length >= 2 &&
      nameVal.length <= MAX_NAME_LENGTH &&
      isUsernameValid();
  };

  const usernameChanged = username.trim() !== originalUsername;
  const usernameLength = username.length;
  const nameLength = name.length;
  const bioLength = bio.length;

  // ── Responsive avatar size ───────────────────────────────────────────────
  // Mobile layout/markup is untouched; this only swaps the avatar's pixel
  // size once the viewport crosses into the desktop layout below.

  useEffect(() => {
    const updateAvatarSize = () => {
      setAvatarSize(window.innerWidth >= DESKTOP_BREAKPOINT ? DESKTOP_AVATAR_SIZE : MOBILE_AVATAR_SIZE);
    };
    updateAvatarSize();
    window.addEventListener('resize', updateAvatarSize);
    return () => window.removeEventListener('resize', updateAvatarSize);
  }, []);

  // ── Load profile ──────────────────────────────────────────────────────────

  useEffect(() => {
    const loadProfile = async () => {
      setIsLoading(true);
      try {
        const profileData = await userProfileService.getCurrentUserProfile();
        if (profileData) {
          setProfile(profileData);
          setName(profileData.name || '');
          setUsername(profileData.username || '');
          setOriginalUsername(profileData.username || '');
          setBio(profileData.bio || '');
          setLink(profileData.links?.[0] || '');
          setCurrentImageUrl(profileData.hasProfileImage ? profileData.profileImage : null);

          // Get last username change
          if (user?.uid) {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const data = userDoc.data();
            if (data?.usernameChangedAt?.toDate) {
              setLastUsernameChange(data.usernameChangedAt.toDate());
            }
          }
        }
      } catch (error) {
        console.error('Load profile error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadProfile();
  }, [user]);

  // ── Username validation ──────────────────────────────────────────────────

  useEffect(() => {
    const validateUsername = async () => {
      const val = username.trim();
      if (!val || val === originalUsername) {
        setUsernameError(null);
        return;
      }

      if (val.length < 2) {
        setUsernameError('Username must be at least 2 characters');
        return;
      }

      const timer = setTimeout(async () => {
        setIsCheckingUsername(true);
        try {
          const q = query(
            collection(db, 'users'),
            where('username', '==', val),
            limit(1)
          );
          const snap = await getDocs(q);
          setUsernameError(snap.empty ? null : 'Username is already taken');
        } catch (error) {
          console.error('Username validation error:', error);
        } finally {
          setIsCheckingUsername(false);
        }
      }, 500);

      return () => clearTimeout(timer);
    };

    validateUsername();
  }, [username, originalUsername]);

  // ── Format phone ─────────────────────────────────────────────────────────

  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/[^\d+]/g, '');
    if (digits.startsWith('+256') && digits.length === 13) {
      return `${digits.substring(0, 4)} ${digits.substring(4, 7)} ${digits.substring(7, 10)} ${digits.substring(10)}`;
    }
    if (digits.startsWith('+1') && digits.length === 12) {
      return `${digits.substring(0, 2)} ${digits.substring(2, 5)} ${digits.substring(5, 8)} ${digits.substring(8)}`;
    }
    return phone;
  };

  // ── Image handlers ──────────────────────────────────────────────────────

  const handlePickImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setPickedImage(preview);
    setPickedImageFile(file);
    e.target.value = '';
  };

  const uploadProfileImage = async (file) => {
    if (!user?.uid) return null;
    try {
      const storageRef = ref(storage, `user_profile_images/${user.uid}.jpg`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Upload image error:', error);
      return null;
    }
  };

  // ── Save profile ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (isSaving || !isFormValid()) return;

    if (!user?.uid) {
      alert('Not authenticated. Please log in again.');
      return;
    }

    setIsSaving(true);

    try {
      let newImageUrl = currentImageUrl;

      if (pickedImageFile) {
        newImageUrl = await uploadProfileImage(pickedImageFile);
        if (!newImageUrl) {
          alert('Failed to upload photo. Try again.');
          setIsSaving(false);
          return;
        }
      }

      const nameVal = name.trim();
      const newUsername = username.trim();
      const bioVal = bio.trim();
      const linkVal = link.trim();

      const existingLinks = profile?.links || [];
      const newLinks = linkVal
        ? [linkVal, ...existingLinks.slice(1)]
        : existingLinks.slice(1);

      const updates = {
        name: nameVal,
        displayName: nameVal,
        bio: bioVal,
        links: newLinks,
        updatedAt: serverTimestamp(),
      };

      if (newImageUrl) {
        updates.profileImage = newImageUrl;
      }

      if (newUsername !== originalUsername && canChangeUsername) {
        updates.username = newUsername;
        updates.usernameChangedAt = serverTimestamp();
      }

      await updateDoc(doc(db, 'users', user.uid), updates);

      // Refresh cached profile
      await userProfileService.getCurrentUserProfile({ forceRefresh: true });

      setCurrentImageUrl(newImageUrl);
      setPickedImage(null);
      setPickedImageFile(null);
      if (newUsername !== originalUsername) {
        setOriginalUsername(newUsername);
        setLastUsernameChange(new Date());
      }

      alert('Profile updated successfully.');
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save changes. Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete account ────────────────────────────────────────────────────────

  const handleDeleteAccount = async () => {
    if (!window.confirm(
      'Are you sure you want to permanently delete your channel?\n\n' +
      'This action is irreversible. All your listings, chats, and personal data will be permanently removed.'
    )) return;

    setIsLoading(true);

    try {
      const uid = user?.uid;
      if (!uid) throw new Error('User not logged in');

      // 1. Delete profile image
      try {
        const storageRef = ref(storage, `user_profile_images/${uid}.jpg`);
        await deleteObject(storageRef);
      } catch (_) {}

      // 2. Delete listings
      const listingsSnap = await getDocs(
        query(collection(db, 'listings'), where('uid', '==', uid))
      );

      for (const listingDoc of listingsSnap.docs) {
        // Delete comments
        const commentsSnap = await getDocs(
          collection(listingDoc.ref, 'comments')
        );
        for (const commentDoc of commentsSnap.docs) {
          await deleteDoc(commentDoc.ref);
        }

        // Delete ratings
        const ratingsSnap = await getDocs(
          collection(listingDoc.ref, 'ratings')
        );
        for (const ratingDoc of ratingsSnap.docs) {
          await deleteDoc(ratingDoc.ref);
        }

        await deleteDoc(listingDoc.ref);
      }

      // 3. Delete chats
      const chatsSnap = await getDocs(
        query(collection(db, 'chats'), where('participants', 'array-contains', uid))
      );

      for (const chatDoc of chatsSnap.docs) {
        const messagesSnap = await getDocs(
          collection(chatDoc.ref, 'messages')
        );
        for (const msgDoc of messagesSnap.docs) {
          await deleteDoc(msgDoc.ref);
        }
        await deleteDoc(chatDoc.ref);
      }

      // 4. Delete notifications
      const notifsSnap = await getDocs(
        collection(db, 'notifications', uid, 'userNotifications')
      );
      for (const notifDoc of notifsSnap.docs) {
        await deleteDoc(notifDoc.ref);
      }
      await deleteDoc(doc(db, 'notifications', uid));

      // 5. Remove user from subscribers/subscribed lists
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const subscribers = data.subscribers || [];
        const subscribed = data.subscribed || [];

        for (const subscriberId of subscribers) {
          await updateDoc(doc(db, 'users', subscriberId), {
            subscribed: arrayRemove([uid]),
          });
        }

        for (const subscribedId of subscribed) {
          await updateDoc(doc(db, 'users', subscribedId), {
            subscribers: arrayRemove([uid]),
          });
        }
      }

      // 6. Delete user document
      await deleteDoc(doc(db, 'users', uid));

      // 7. Delete Firebase Auth user
      await user.delete();

      // 8. Clear local data
      await authService.logout();
      userProfileService.clearCache();
      await chatLocalDb.clearAll();

      navigate('/', { replace: true });
    } catch (error) {
      console.error('Delete account error:', error);
      setIsLoading(false);
      alert('Failed to delete account. Please try again.');
    }
  };

  // ── Logout ───────────────────────────────────────────────────────────────

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to log out?')) return;

    await authService.logout();
    userProfileService.clearCache();
    await chatLocalDb.clearAll();

    navigate('/', { replace: true });
  };

  // ── Copy phone ──────────────────────────────────────────────────────────

  const copyPhoneToClipboard = () => {
    const phone = profile?.phone || '';
    if (phone) {
      navigator.clipboard.writeText(phone);
      alert('Phone number copied to clipboard.');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen bg-filatom-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const formattedPhone = formatPhone(profile?.phone || '');
  const showUsernameCounter = usernameLength > 0 &&
    !isCheckingUsername &&
    !(usernameChanged && !usernameError && usernameLength >= 2);

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* App Bar
          Desktop: contents center to the same column as the page body and
          gain a hairline bottom border, and a centered title appears (it's
          a brand-new element gated behind `hidden md:block`, so it renders
          nothing extra on mobile). */}
      <div className="flex items-center justify-between px-4 py-2 bg-filatom-bg md:px-10 md:py-4 md:border-b md:border-white/5">
        <button
          onClick={() => navigate(-1)}
          className="text-white text-base font-normal md:text-sm"
        >
          Cancel
        </button>
        <span className="hidden md:block text-white text-sm font-semibold tracking-wide">
          Edit profile
        </span>
        <button
          onClick={handleSave}
          disabled={isSaving || !isFormValid()}
          className={`text-white text-base font-normal md:text-sm md:font-semibold ${
            isSaving || !isFormValid() ? 'opacity-50' : ''
          }`}
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            'Save'
          )}
        </button>
      </div>

      {/* Content
          Desktop: the page is contained to a centered column, and the
          avatar block becomes a sticky left-hand sidebar next to the form
          fields instead of sitting stacked above them. All of this rides
          on `md:` variants layered onto the original mobile classes, so
          nothing below md:768px changes from the original markup. */}
      <div className="px-4 pb-8 md:max-w-3xl md:mx-auto md:px-10 md:pt-10 md:pb-16">
        <div className="flex flex-col md:flex-row md:items-start md:gap-14">

          {/* Avatar */}
          <div className="flex flex-col items-center mt-5 md:mt-0 md:w-[180px] md:flex-shrink-0 md:sticky md:top-10">
            <button onClick={handlePickImage}>
              {pickedImage ? (
                <img
                  src={pickedImage}
                  alt="Profile"
                  className="w-25 h-25 md:w-[130px] md:h-[130px] rounded-full object-cover"
                />
              ) : (
                <UserAvatar
                  size={avatarSize}
                  imageUrl={currentImageUrl}
                  name={profile?.displayName}
                />
              )}
            </button>
            <button onClick={handlePickImage} className="text-filatom-primary text-sm mt-2.5">
              Set new photo
            </button>
          </div>

          {/* Form fields */}
          <div className="space-y-3 mt-7 md:mt-0 md:flex-1 md:min-w-0 md:space-y-4">
            {/* Name */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: COLORS.card }}>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={MAX_NAME_LENGTH}
                  placeholder="Name"
                  className="flex-1 bg-transparent text-white text-sm outline-none px-4 py-4 placeholder:text-[#6B6E75]"
                />
                {nameLength > 0 && (
                  <span className="text-[#6B6E75] text-[11px] pr-4">
                    {nameLength}/{MAX_NAME_LENGTH}
                  </span>
                )}
              </div>
              <div className="h-px bg-white/5 mx-4" />

              {/* Username */}
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^a-zA-Z0-9_\-.]/g, '');
                    setUsername(val);
                  }}
                  maxLength={MAX_USERNAME_LENGTH}
                  placeholder="Username"
                  disabled={!canChangeUsername}
                  className="flex-1 bg-transparent text-white text-sm outline-none px-4 py-4 placeholder:text-[#6B6E75] disabled:text-white/40"
                />
                {isCheckingUsername ? (
                  <div className="pr-4">
                    <div className="w-4 h-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  usernameChanged && !usernameError && usernameLength >= 2 && (
                    <span className="text-filatom-primary pr-4">
                      <svg className="w-4.5 h-4.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )
                )}
                {showUsernameCounter && (
                  <span className="text-[#6B6E75] text-[11px] pr-4">
                    {usernameLength}/{MAX_USERNAME_LENGTH}
                  </span>
                )}
              </div>

              {(usernameError || !canChangeUsername) && (
                <div className="px-4 pb-2.5">
                  <span className="text-red-500 text-xs">
                    {usernameError ||
                      `Username can be changed again in ${daysUntilUsernameChange} day${daysUntilUsernameChange === 1 ? '' : 's'}`}
                  </span>
                </div>
              )}
            </div>

            {/* Bio */}
            <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: COLORS.card }}>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                maxLength={MAX_BIO_LENGTH}
                placeholder="Bio"
                rows={4}
                className="w-full bg-transparent text-white text-sm outline-none px-4 py-4 placeholder:text-[#6B6E75] resize-none"
              />
              {bioLength > 0 && (
                <span className="absolute top-3 right-4 text-[#6B6E75] text-[11px]">
                  {bioLength}/{MAX_BIO_LENGTH}
                </span>
              )}
            </div>

            {/* Link */}
            <div className="rounded-full overflow-hidden" style={{ backgroundColor: COLORS.card }}>
              <input
                type="text"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="Link"
                className="w-full bg-transparent text-white text-sm outline-none px-4 py-4 placeholder:text-[#6B6E75]"
              />
            </div>

            {/* Phone & Group */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: COLORS.card }}>
              <button
                onClick={copyPhoneToClipboard}
                className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-white/5"
              >
                <span className="text-[#6B6E75] text-sm">Phone number</span>
                <span className="text-white text-sm flex items-center gap-1">
                  {formattedPhone}
                  <svg className="w-4.5 h-4.5 text-[#6B6E75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </span>
              </button>
              <div className="h-px bg-white/5" />
              <button className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-white/5">
                <span className="text-[#6B6E75] text-sm">Group</span>
                <span className="text-white text-sm flex items-center gap-1">
                  Add
                  <svg className="w-5 h-5 text-[#6B6E75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </button>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full py-4 rounded-full text-red-500 text-sm font-normal"
              style={{ backgroundColor: COLORS.card }}
            >
              Log out
            </button>

            {/* Delete Account */}
            <button
              onClick={handleDeleteAccount}
              className="w-full py-4 rounded-full text-red-500 text-sm font-semibold border border-red-500/30"
              style={{ backgroundColor: 'rgba(255,23,68,0.12)' }}
            >
              Delete Channel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EditProfileScreen;